import { IRecordCompositeScoreInput } from '../../Score/ManageEvaluations/manage-evaluations.interface';
import { scoreFromAction } from '../../../lib/utils/user-session.service';
import { unavailableFilenamesFor } from '../../../lib/utils/video.utils';
import { ICalibrationClipGroup } from './assignment-scoring.types';

// The minimal score shape the Evaluate* widgets read (`score.raw`), from a raw value.
// Takes the raw string (not the op) so the call site memoizes on the primitive.
export const rawAsWidgetScore = (raw?: string): Pick<IScore, 'raw'> | undefined =>
  raw == null ? undefined : { raw };

// The composite-score payload of a buffered action IF it belongs to this assignment
// (records and removals); undefined otherwise. Single owner for "is this action mine?".
export const assignmentScoreFromAction = (
  action: IUserAction,
  assignmentId?: string,
): IRecordCompositeScoreInput | undefined => {
  if (action.actionType !== 'compositeScore' && action.actionType !== 'removeCompositeScore') return undefined;
  const score = scoreFromAction(action);
  if (!assignmentId || score?.additionalInfo?.calibrationAssignmentId !== assignmentId) return undefined;
  return score;
};

// Single owner of the video+skill composite key used to index buffered scores.
export const makeVideoSkillKey = (item: { calibrationVideoId: string; skillId: string }): string =>
  `${item.calibrationVideoId}:${item.skillId}`;

// Single owner of "is this item scored?": a server compositeScoreId, or — when the
// optional buffer map is passed — an unsynced device score keyed by video+skill.
export const isItemScored = (
  item: ICalibrationAssignmentItem,
  bufferedByKey: { [videoSkillKey: string]: IRecordCompositeScoreInput } = {},
): boolean => !!item.compositeScoreId || !!bufferedByKey[makeVideoSkillKey(item)];

// Group items by clip filename (one video, its skills beneath). Excludes filename-less items
// and clips in `unavailableFilenames` — never score a video that cannot play. See CLAUDE.md.
export const groupItemsByClip = (
  assignment: ICalibrationAssignment | undefined,
  unavailableFilenames?: ReadonlySet<string>,
): ICalibrationClipGroup[] => {
  const groups: ICalibrationClipGroup[] = [];
  const byKey: { [key: string]: ICalibrationClipGroup } = {};
  ((assignment && assignment.items) || []).forEach((item) => {
    const filename = item.calibrationVideo && item.calibrationVideo.filename;
    if (!filename) return;
    if (unavailableFilenames && unavailableFilenames.has(filename)) return;
    if (!byKey[filename]) {
      byKey[filename] = { key: filename, calibrationVideo: item.calibrationVideo, drill: item.drill, items: [] };
      groups.push(byKey[filename]);
    }
    byKey[filename].items.push(item);
  });
  return groups;
};

// The playable-clip filenames on an assignment (truthy only) — the input to availability lookups.
export const clipFilenames = (assignment: ICalibrationAssignment | undefined): string[] =>
  ((assignment && assignment.items) || [])
    .map((item) => item.calibrationVideo && item.calibrationVideo.filename)
    .filter((filename): filename is string => !!filename);

// Single owner of an assignment's clip progress (total/scored), reading the shared availability
// cache so the list summary and the scorer denominator can't drift. See CLAUDE.md.
export const assignmentClipProgress = (
  assignment: ICalibrationAssignment | undefined,
  bufferedByKey: { [videoSkillKey: string]: IRecordCompositeScoreInput } = {},
): { total: number; scored: number } =>
  progressFromGroups(
    groupItemsByClip(assignment, unavailableFilenamesFor(clipFilenames(assignment))),
    bufferedByKey,
  );

// A clip is scored only when every skill beneath it has a server or buffered score — the gate
// that lets auto-progress move to the next clip (all skills on a page must be done first).
export const isGroupScored = (
  group: ICalibrationClipGroup,
  bufferedByKey: { [videoSkillKey: string]: IRecordCompositeScoreInput } = {},
): boolean => group.items.every((item) => isItemScored(item, bufferedByKey));

// One owner of the clips-scored progress projection, shared by the list summary and the
// scoring screen header so the two surfaces can't drift on what counts as scored.
export const progressFromGroups = (
  groups: ICalibrationClipGroup[],
  bufferedByKey: { [videoSkillKey: string]: IRecordCompositeScoreInput } = {},
): { total: number; scored: number } => ({
  total: groups.length,
  scored: groups.filter((group) => isGroupScored(group, bufferedByKey)).length,
});

// 0-based index of the first clip not fully scored, so the page resumes where the evaluator
// left off; groups.length (the completion screen) when every clip is scored.
export const firstUnscoredGroupIndex = (
  groups: ICalibrationClipGroup[],
  bufferedByKey: { [videoSkillKey: string]: IRecordCompositeScoreInput },
): number => {
  const idx = groups.findIndex((group) => !isGroupScored(group, bufferedByKey));
  return idx === -1 ? groups.length : idx;
};

// Next not-fully-scored clip searching forward from (and excluding) fromIndex, wrapping to
// the front; null when every other clip is scored. fromIndex excluded: it was just completed.
export const nextUnscoredGroupIndex = (
  groups: ICalibrationClipGroup[],
  bufferedByKey: { [videoSkillKey: string]: IRecordCompositeScoreInput },
  fromIndex: number,
): number | null => {
  const total = groups.length;
  for (let offset = 1; offset < total; offset += 1) {
    const idx = (fromIndex + offset) % total;
    if (!isGroupScored(groups[idx], bufferedByKey)) return idx;
  }
  return null;
};
